import React, { useState, useEffect, useContext } from 'react';
import { fmtDate as _fmtDate } from '@/lib/dates';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { AuthContext, API } from '@/App';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Plus, Eye, FileText, AlertCircle, CheckCircle } from 'lucide-react';

const BankPMInspectionList = () => {
  const { token } = useContext(AuthContext);
  const navigate = useNavigate();
  const [inspections, setInspections] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [locations, setLocations] = useState([]);
  const [selectedPM, setSelectedPM] = useState(null);
  const [isViewDialogOpen, setIsViewDialogOpen] = useState(false);

  useEffect(() => {
    fetchInspections();
    fetchCustomers();
    fetchLocations();
  }, []);

  const fetchInspections = async () => {
    try {
      const response = await axios.get(`${API}/bank-pm-inspections`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setInspections(response.data);
    } catch (error) {
      toast.error('Failed to load PM inspections');
    }
  };

  const fetchCustomers = async () => {
    try {
      const response = await axios.get(`${API}/customers`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCustomers(response.data);
    } catch (error) {
      console.error('Failed to fetch customers');
    }
  };

  const fetchLocations = async () => {
    try {
      const response = await axios.get(`${API}/locations`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLocations(response.data);
    } catch (error) {
      console.error('Failed to fetch locations');
    }
  };

  const handleView = async (pmId) => {
    try {
      const response = await axios.get(`${API}/bank-pm-inspections/${pmId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSelectedPM(response.data);
      setIsViewDialogOpen(true);
    } catch (error) {
      toast.error('Failed to load PM details');
    }
  };

  const getBankName = (customerId) => {
    const customer = customers.find(c => c.id === customerId);
    return customer ? customer.name : 'Unknown';
  };

  const getBranchName = (locationId) => {
    const location = locations.find(l => l.id === locationId);
    return location ? location.name : 'Unknown';
  };

  const countIssues = (pm) => {
    let count = 0;
    const allItems = [...pm.exterior_items, ...pm.interior_items, ...pm.safety_operational_items];
    allItems.forEach(item => {
      if (item.status === 'needs_attention') count++;
    });
    return count;
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-xl font-bold" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
            Bank PM Inspections
          </h2>
          <p className="text-sm text-slate-600">Preventive Maintenance for Banks</p>
        </div>
        <Button
          onClick={() => navigate('/field/bank-pm-inspection/new')}
          style={{ backgroundColor: '#6B9F3E' }}
          data-testid="create-pm-btn"
        >
          <Plus className="h-4 w-4 mr-2" />
          New PM
        </Button>
      </div>

      <div className="space-y-3">
        {inspections.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <FileText className="h-12 w-12 mx-auto text-slate-300 mb-3" />
              <p className="text-slate-500">No PM inspections yet.</p>
            </CardContent>
          </Card>
        ) : (
          inspections.map((pm) => {
            const issuesCount = countIssues(pm);
            return (
              <Card 
                key={pm.id} 
                className="hover:shadow-md transition-shadow"
                style={{ borderLeft: `4px solid ${issuesCount > 0 ? '#F59E0B' : '#6B9F3E'}` }}
              >
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <CardTitle className="text-base">{pm.inspection_number}</CardTitle>
                        {issuesCount > 0 && (
                          <Badge className="bg-orange-100 text-orange-800">{issuesCount} issue{issuesCount > 1 ? 's' : ''}</Badge>
                        )}
                        {pm.forward_to_operations && (
                          <Badge className="bg-red-100 text-red-800">Ops</Badge>
                        )}
                      </div>
                      <p className="text-sm text-slate-600">
                        {getBankName(pm.bank_customer_id)} - {getBranchName(pm.branch_location_id)}
                      </p>
                      <p className="text-xs text-slate-500 mt-1">{_fmtDate(pm.date_of_inspection)}</p>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleView(pm.id)}
                    >
                      <Eye className="h-4 w-4" />
                    </Button>
                  </div>
                </CardHeader>
              </Card>
            );
          })
        )}
      </div>

      <Dialog open={isViewDialogOpen} onOpenChange={setIsViewDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>PM Inspection Details</DialogTitle>
          </DialogHeader>
          {selectedPM && (
            <div className="space-y-4">
              <div className="bg-slate-50 p-4 rounded-lg">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-xs">PM Number</Label>
                    <p className="font-bold">{selectedPM.inspection_number}</p>
                  </div>
                  <div>
                    <Label className="text-xs">Bank</Label>
                    <p className="font-medium">{getBankName(selectedPM.bank_customer_id)}</p>
                  </div>
                  <div>
                    <Label className="text-xs">Branch</Label>
                    <p className="font-medium">{getBranchName(selectedPM.branch_location_id)}</p>
                  </div>
                  <div>
                    <Label className="text-xs">Inspector</Label>
                    <p className="font-medium">{selectedPM.inspector_name}</p>
                  </div>
                </div>
              </div>
              <p className="text-sm">Total items inspected: {selectedPM.exterior_items.length + selectedPM.interior_items.length + selectedPM.safety_operational_items.length}</p>
              {selectedPM.general_notes && (
                <div>
                  <Label className="text-xs">Notes</Label>
                  <p className="mt-1 p-3 bg-slate-50 rounded">{selectedPM.general_notes}</p>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BankPMInspectionList;
